import type { InboxNotificationData } from '@liveblocks/client';
import { useInboxNotifications } from '@liveblocks/react';
import { Text } from '@mantine/core';
import {
  formatOwnerName,
  formatUserName,
  getListingName,
} from '@vrplatform/ui-common';
import { type Maybe, hasValue, isUUID, uniqueBy, utc } from '@vrplatform/utils';
import type { ReactNode } from 'react';
import { useQuery } from '../graphql';

type NotificationIcon =
  | 'update'
  | 'insert'
  | 'sync'
  | 'fetch'
  | 'delete'
  | 'resolve'
  | 'error'
  | 'refresh';

export type NotificationRow = InboxNotificationData & {
  actor?: {
    id: string;
    firstName: Maybe<string>;
    lastName: Maybe<string>;
    email: string;
    avatar?: string;
  };
  to?: string;
  icon?: NotificationIcon;
  content: ReactNode;
};

const NOTIFICATION_QUERY_KEY = 'notifications';

export function useNotificationsQuery() {
  const {
    inboxNotifications,
    isLoading: loadingInbox,
    error: liveBlocksError,
  } = useInboxNotifications();
  const {
    data: queryData,
    isLoading: loadingQuery,
    error,
    isInitialLoading,
    refetch,
  } = useQuery(
    (q, args) => {
      if (!args.notifications?.length) {
        return {
          all: [],
          unread: [],
        };
      }

      const userIds = uniqueBy(
        args.notifications
          .map((notification) => {
            if (
              notification.kind === '$ownerStatementInReview' ||
              notification.kind === '$ownerStatementPublished'
            ) {
              const data = notification.activities[0].data;

              if ('triggeredByUserId' in data) {
                return data.triggeredByUserId;
              }
            }

            return undefined;
          })
          .filter(hasValue)
      );

      const actors = q
        .user({
          where: {
            id: { _in: userIds },
          },
        })
        .map((user) => ({
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email!,
          avatar: undefined,
        }));

      const formattedRows = args.notifications.map<NotificationRow | null>(
        (notification) => {
          if (
            notification.kind === '$ownerStatementInReview' ||
            notification.kind === '$ownerStatementPublished' ||
            notification.kind === '$inviteOwnerAccepted' ||
            notification.kind === '$inviteTeamMemberAccepted'
          ) {
            const data = notification.activities[0].data;
            let actor: NotificationRow['actor'];

            if ('triggeredByUserId' in data) {
              actor = actors.find((item) => item.id === data.triggeredByUserId);
            }

            const formatted: {
              content: NotificationRow['content'];
              to?: NotificationRow['to'];
            } | null = (() => {
              if ('ownerStatementId' in data && data.ownerStatementId) {
                const statement = q
                  .ownerStatements({
                    where: {
                      id: { _eq: data.ownerStatementId },
                    },
                    limit: 1,
                  })
                  .map((st) => ({
                    periodId: st.listingOwnershipPeriodId,
                    listingName: `${getListingName(st.listingOwnershipPeriod?.listing, { allowEmpty: true })} - ${utc(st.startAt).format('MM/YYYY')}`,
                  }))[0];

                if (!statement) {
                  return null;
                }

                return {
                  content: (
                    <Text c="gray">
                      Updated{' '}
                      <Text span fw={500} c="dark">
                        {statement.listingName || 'owner statement'}
                      </Text>{' '}
                      to{' '}
                      {notification.kind === '$ownerStatementInReview'
                        ? 'in review'
                        : 'published'}
                    </Text>
                  ),
                  to: `/statement/${statement.periodId}?date=${utc(data.startAt).format('YYYY-MM-01')}&sti=${data.tenantId}`,
                };
              }

              if ('taxStatementId' in data && data.taxStatementId) {
                const statement = q
                  .taxStatements({
                    where: {
                      id: { _eq: data.taxStatementId },
                    },
                    limit: 1,
                  })
                  .map(
                    (st) => `${formatOwnerName(st.owner)} - ${data.year}`
                  )[0];

                if (!statement) {
                  return null;
                }

                return {
                  content: (
                    <Text c="gray">
                      Updated{' '}
                      <Text span fw={500} c="dark">
                        {statement || 'tax statement'}
                      </Text>{' '}
                      to {data.status === 'ready' ? 'ready' : 'filed'}
                    </Text>
                  ),
                  to: `/statements/tax-statements?year=${data.year}&status=${data.status}`,
                };
              }

              if ('userId' in data) {
                const user = !isUUID(data.userId)
                  ? ''
                  : q
                      .user({
                        where: {
                          id: { _eq: data.userId },
                        },
                        limit: 1,
                      })
                      .map((user) => formatUserName(user))[0];

                if (!user) {
                  return null;
                }

                const defaultName =
                  notification.kind === '$inviteOwnerAccepted'
                    ? 'Owner'
                    : 'Member';

                return {
                  content: (
                    <Text c="gray">
                      <Text span fw={500} c="dark">
                        {user || defaultName}
                      </Text>{' '}
                      accepted{' '}
                      {notification.kind === '$inviteOwnerAccepted'
                        ? 'an invitation to the owner portal'
                        : 'an invitation to the team'}
                    </Text>
                  ),
                };
              }

              return {
                content: <Text c="gray">Missing notification</Text>,
              };
            })();

            if (!formatted) {
              return null;
            }

            return {
              ...notification,
              actor,
              icon: 'update',
              content: formatted.content,
              to: formatted.to,
            };
          }

          return {
            ...notification,
            content: <Text>Missing notification</Text>,
          };
        }
      );

      const all = formattedRows.filter(hasValue);

      return {
        all,
        unread: all.filter((row) => !row.readAt),
      };
    },
    {
      queryKey: [NOTIFICATION_QUERY_KEY],
      skip: !inboxNotifications?.length,
      keepPreviousData: true,
      variables: {
        notifications: inboxNotifications,
      },
    }
  );

  return {
    loading: loadingInbox || loadingQuery,
    isInitialLoading,
    error: liveBlocksError || error || undefined,
    queryData,
    refetch,
  };
}
